import { CampingSpot } from '../types';

/**
 * Behovsstyrt berikelse ("ikke-masete").
 *
 * Returnerer DEN ENE høyest-prioriterte opplysningen en plass mangler, eller null
 * hvis plassen er komplett nok til at vi IKKE skal spørre. Prinsippet er: spør aldri
 * om noe vi allerede har, og spør aldri om mer enn én ting av gangen.
 *
 * Fordi behovet beregnes fra plassdata (ikke per bruker), fordeles berikelsesarbeidet
 * automatisk: hopper én bruker over, ser neste bruker som sjekker inn fortsatt behovet.
 */
export type EnrichmentNeed = 'photo' | 'rating' | null;

// Har plassen et EKTE brukerbilde? Auto-hentede bilder (auto_image_url, Wikimedia/
// Mapillary innenfor 100–2000 m) teller bevisst IKKE — vi vil fortsatt ha et faktisk
// bilde av selve plassen.
const hasRealPhoto = (spot: CampingSpot): boolean =>
  !!((spot.photoUrls && spot.photoUrls.length > 0) || spot.photoUrl || spot.ugcThumbnailUrl);

const reviewCountOf = (spot: CampingSpot): number =>
  typeof spot.reviewCount === 'number' ? spot.reviewCount : (spot.reviews?.length ?? 0);

export const getSpotEnrichmentNeed = (spot?: CampingSpot | null): EnrichmentNeed => {
  if (!spot) return null;
  // Slettede/ugyldige plasser berikes aldri.
  if (spot.isDeleted || spot.isInvalid) return null;
  if (!hasRealPhoto(spot)) return 'photo';
  if (reviewCountOf(spot) === 0) return 'rating';
  return null;
};

export const spotNeedsEnrichment = (spot?: CampingSpot | null): boolean =>
  getSpotEnrichmentNeed(spot) !== null;

/**
 * Forsidebilde(r) som ligger MATERIALISERT direkte på plass-doc'en (ikke i
 * `images`-underkolleksjonen). Kollapsede feed-/listekort abonnerer ikke på
 * underkolleksjonen (kun når kortet er valgt), så uten disse feltene faller de
 * tilbake til satellitt selv om plassen har ekte bilder. `ugcThumbnailUrl`
 * (første opplastede UGC-bilde, satt ved opplasting eller backfill) prioriteres
 * foran `photoUrl`. Auto-hentede bilder (`auto_image_url`) tas bevisst IKKE med
 * her — de håndteres som eget lag i SpotCard. Trimmet og deduplisert; bevarer
 * rekkefølgen.
 */
export const materializedSpotCovers = (spot?: CampingSpot | null): string[] => {
  if (!spot) return [];
  const trim = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');
  const out: string[] = [];
  for (const url of [trim(spot.ugcThumbnailUrl), trim(spot.photoUrl)]) {
    if (url && !out.includes(url)) out.push(url);
  }
  return out;
};

/**
 * Berikelses-versjon for on-demand Google-berikelse (fetchGooglePlacePhotos).
 * MÅ holdes i sync med ENRICH_VERSION i functions/index.js. Bump når serverlogikken
 * forbedres slik at plasser som alt er «sjekket» berikes på nytt én gang ved neste
 * kort-visning. 2 = Gemini-generert beskrivelse + Mapillary-bilde-fallback.
 */
export const ENRICH_VERSION = 2;

const GOOGLE_PHOTO_REFRESH_MS = 1000 * 60 * 60 * 24 * 180; // 6 mnd

/**
 * Skal kortet be serveren om (re-)berikelse? Ja når plassen aldri er sjekket, når
 * 6-mnd-cachen er utløpt, ELLER når berikelsesversjonen er eldre enn gjeldende
 * (ny serverlogikk skal nå eldre docs uten å vente på at cachen utløper). Serveren
 * har sin egen cache-/versjonsvakt, så et kall her er trygt idempotent.
 */
export const needsGooglePhotoRefresh = (spot?: CampingSpot | null): boolean => {
  if (!spot) return false;
  const checkedAt = spot.googlePhotosCheckedAt;
  if (!checkedAt) return true;
  if (Date.now() - checkedAt > GOOGLE_PHOTO_REFRESH_MS) return true;
  return spot.enrichVersion !== ENRICH_VERSION;
};
